import { useState, useEffect } from 'react';
import { Loader2, Play, Database, Search, Plus, Minus, CheckSquare, Square } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authedFetch } from '@/lib/api';
import { logger } from '@/lib/logger';
import type { SearchEvalDataset } from '@/types/dashboard';

export default function ConfigurationPanel() {
  const navigate = useNavigate();

  // State
  const [datasets, setDatasets] = useState<SearchEvalDataset[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [runsPerDataset, setRunsPerDataset] = useState(1);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await authedFetch('/api/evaluation/search-datasets');
      if (response.ok) {
        const data = await response.json();
        const ds = data.datasets || [];
        setDatasets(ds);
        // Select all by default
        setSelectedIds(new Set(ds.map((d: SearchEvalDataset) => d.id)));
      }
    } catch (err) {
      logger.error('Failed to load datasets:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleDataset = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === datasets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(datasets.map((d) => d.id)));
    }
  };

  const startEvaluation = async () => {
    if (selectedIds.size === 0) return;

    setRunning(true);
    setError(null);
    setSuccess(null);

    try {
      const datasetIds = Array.from(selectedIds);

      const response = await authedFetch('/api/evaluation/search-eval/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dataset_ids: datasetIds, runs_per_dataset: runsPerDataset }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Failed to start evaluation (${response.status})`);
      }

      const data = await response.json();
      setSuccess(`Evaluation started across ${datasetIds.length} dataset${datasetIds.length > 1 ? 's' : ''}! Run ID: ${data.run_id || 'N/A'}`);

      // Navigate to dashboard after 1 second
      setTimeout(() => navigate('/dashboard'), 1000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to start evaluation';
      setError(message);
    } finally {
      setRunning(false);
    }
  };

  const totalPositives = datasets
    .filter((d) => selectedIds.has(d.id))
    .reduce((sum, d) => sum + d.positive_count, 0);
  const totalNegatives = datasets
    .filter((d) => selectedIds.has(d.id))
    .reduce((sum, d) => sum + d.negative_count, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold bg-linear-to-r from-primary via-orange-400 to-amber-300 bg-clip-text text-transparent mb-2">
          Search Evaluation
        </h1>
        <p className="text-muted-foreground">
          Run precision/recall evaluations against ground-truth datasets
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-destructive/20 border border-destructive/40 text-destructive rounded-xl backdrop-blur-xs">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-success/20 border border-success/40 text-success rounded-xl backdrop-blur-xs">
          {success}
        </div>
      )}

      <div className="rounded-xl border border-primary/20 bg-card/50 p-6 backdrop-blur-xs space-y-6">
        {/* Dataset Selection */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Database className="h-4 w-4 text-primary" />
              Ground Truth Datasets
            </label>
            {datasets.length > 0 && (
              <button
                onClick={toggleAll}
                disabled={running}
                className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-orange-300 transition-colors disabled:opacity-50"
              >
                {selectedIds.size === datasets.length ? (
                  <>
                    <CheckSquare className="h-3.5 w-3.5" />
                    Deselect all
                  </>
                ) : (
                  <>
                    <Square className="h-3.5 w-3.5" />
                    Select all
                  </>
                )}
              </button>
            )}
          </div>

          {datasets.length > 0 ? (
            <div className="space-y-1.5">
              {datasets.map((ds) => {
                const isSelected = selectedIds.has(ds.id);
                return (
                  <button
                    key={ds.id}
                    onClick={() => toggleDataset(ds.id)}
                    disabled={running}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      isSelected
                        ? 'border-primary/40 bg-primary/10'
                        : 'border-border/30 bg-background/30 hover:border-primary/20'
                    } disabled:opacity-50`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="shrink-0">
                        {isSelected ? (
                          <CheckSquare className="h-4.5 w-4.5 text-primary" />
                        ) : (
                          <Square className="h-4.5 w-4.5 text-muted-foreground/50" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-foreground truncate">
                          {ds.name}
                        </div>
                        {ds.description && (
                          <div className="text-xs text-muted-foreground truncate mt-0.5">
                            {ds.description}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0 text-xs">
                        <span className="flex items-center gap-1">
                          <Plus className="h-3 w-3 text-green-400" />
                          <span className="font-bold text-green-400">{ds.positive_count}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <Minus className="h-3 w-3 text-red-400" />
                          <span className="font-bold text-red-400">{ds.negative_count}</span>
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Database className="h-10 w-10 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No datasets available</p>
            </div>
          )}
        </div>

        {/* Selection Summary */}
        {selectedIds.size > 0 && (
          <div className="rounded-lg border border-primary/10 bg-background/30 p-3 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              <span className="font-bold text-foreground">{selectedIds.size}</span> of{' '}
              {datasets.length} datasets selected
            </span>
            <div className="flex gap-4 text-sm">
              <span>
                <span className="font-bold text-green-400">{totalPositives}</span>{' '}
                <span className="text-muted-foreground">positives</span>
              </span>
              <span>
                <span className="font-bold text-red-400">{totalNegatives}</span>{' '}
                <span className="text-muted-foreground">negatives</span>
              </span>
            </div>
          </div>
        )}

        {/* Runs per Dataset */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Runs per Dataset
          </label>
          <p className="text-xs text-muted-foreground mb-3">
            Run each dataset multiple times to measure consistency. Results show mean &plusmn; stddev and per-venue hit rates.
          </p>
          <div className="flex items-center gap-3">
            {[1, 2, 3, 5, 10].map((n) => (
              <button
                key={n}
                onClick={() => setRunsPerDataset(n)}
                disabled={running}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${
                  runsPerDataset === n
                    ? 'border-primary bg-primary/20 text-primary shadow-xs'
                    : 'border-border/30 bg-background/30 text-muted-foreground hover:border-primary/30 hover:text-foreground'
                } disabled:opacity-50`}
              >
                {n === 1 ? '1x' : `${n}x`}
              </button>
            ))}
          </div>
        </div>

        {/* Start Button */}
        <div className="pt-2">
          <button
            onClick={startEvaluation}
            disabled={running || selectedIds.size === 0}
            className="w-full py-4 rounded-xl bg-linear-to-r from-primary to-orange-400 text-white font-bold text-lg hover:from-primary/90 hover:to-orange-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3"
          >
            {running ? (
              <>
                <Loader2 className="h-6 w-6 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Play className="h-6 w-6" />
                Run {selectedIds.size} Dataset{selectedIds.size !== 1 ? 's' : ''}
                {runsPerDataset > 1 ? ` (${runsPerDataset}x each)` : ''}
              </>
            )}
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 backdrop-blur-xs">
          <h3 className="font-semibold text-orange-300 mb-2 flex items-center gap-2">
            <Search className="h-4 w-4" />
            How it works
          </h3>
          <ul className="text-sm text-orange-400/80 space-y-1">
            <li>Phase 1: Semantic vector search retrieves initial candidates</li>
            <li>Phase 2: LLM reranker filters and scores the candidates</li>
            <li>Results compared against ground truth for precision/recall</li>
            <li>Reranker damage shows true positives incorrectly removed</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
